/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import i18n from './i18n/i18n'

export interface StateOptions {
  [key: string]: string
}

export interface AsyncPickerOption {
  value: string | number
  label: string
  propertyType?: string | null
}

export interface AsyncPickerResponse {
  options: AsyncPickerOption[]
  hasMore: boolean
}

export interface AsyncPickerRequestParams {
  pageStart: number
  pageSize: number
  [key: string]: unknown
}

export interface AsyncPickerRequestResponse<T = unknown> {
  elements: T[]
  totalElements: number
}

export interface AsyncPickerElement {
  id: string | number
  name: string
  type?: string
}

export const FILE_LIFECYCLE_STATES = [
  'DRAFT',
  'HELPER_EXPIRING',
  'COMPLETED',
  'EXPIRED',
  'EFFECTIVE',
  'APPROVED',
  'REJECTED',
  'OBSOLETE',
  'RETIRED'
] as const

export type FileLifecycleState = (typeof FILE_LIFECYCLE_STATES)[number]

export function getStateOptions(states: FileLifecycleState[]): StateOptions {
  const options: StateOptions = { '': 'OPTION_ALL_STATES' }

  FILE_LIFECYCLE_STATES.forEach((state) => {
    if (states.includes(state)) {
      options[state] = i18n.t(`STATE_${state}`)
    }
  })

  return options
}

export async function processAsyncPickerRequest<T extends AsyncPickerElement>(
  requestPromise: Promise<AsyncPickerRequestResponse<T>>,
  params: AsyncPickerRequestParams
): Promise<AsyncPickerResponse> {
  try {
    const response = await requestPromise

    const hasMore = response.elements.length > 0 && (params.pageStart + 1) * params.pageSize < response.totalElements

    const options = response.elements
      .map((item) => ({
        value: item.id,
        label: item.name,
        propertyType: item.type ?? null
      }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

    return {
      options,
      hasMore
    }
  } catch (error) {
    console.error('Error processing async picker request:', error)
    throw error
  }
}
